import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ChartPieIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import CourseProgress from "./CourseProgress";
import { getSessionId, identityHeaders } from "../../../utils/session";
import { Link } from "react-router-dom";

// The notepad used to be four tabs, but two of them ("My Examples" and "Study
// Notes") were reading the same table from two different screens, and the
// vocabulary lived behind a third. Everything a student saves now lands in one
// chronological table: a word from a double-click, a sentence they wrote, with the
// type as a column and a filter above it. The tabs are gone — the filters are all
// the navigation that is left.
const API = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 10;

const TYPES = {
  vocabulary: {
    label: "Vocabulary",
    chip: "bg-indigo-100 text-indigo-700",
    dot: "bg-indigo-500",
    icon: BookOpenIcon,
  },
  sentence: {
    label: "Sentence",
    chip: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-500",
    icon: PencilSquareIcon,
  },
};

const FILTERS = [
  { id: "all", label: "Everything" },
  { id: "vocabulary", label: "Vocabulary" },
  { id: "sentence", label: "Sentences" },
];

// the shared helper: a token when signed in, the visitor's id when not
const authHeaders = () => identityHeaders();

const when = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  if (seconds < 604800) return `${Math.round(seconds / 86400)} d ago`;
  return date.toLocaleDateString();
};

const NotepadOverview = ({ initialFilter = "all", initialView = "notes" }) => {
  const [view, setView] = useState(initialView);
  // a guest can keep working; the account is an offer here, never a wall
  const [showGuestNudge, setShowGuestNudge] = useState(true);
  const isGuest = !localStorage.getItem("jstoken");
  const [vocabulary, setVocabulary] = useState([]);
  const [sentences, setSentences] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders();
      const [wordsRes, sentencesRes] = await Promise.all([
        fetch(`${API}/dictionnary/user`, { headers }),
        fetch(`${API}/userupload`, { headers }),
      ]);

      const words = wordsRes.ok
        ? (await wordsRes.json()).dictionnary || []
        : [];
      const uploads = sentencesRes.ok
        ? (await sentencesRes.json()).userUploads || []
        : [];

      setVocabulary(words);
      setSentences(uploads);
    } catch (err) {
      setError("Could not load your notepad.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // one row per saved thing, newest first: the order they were added
  const rows = useMemo(() => {
    const words = vocabulary.map((item) => ({
      key: `word-${item.word_id}`,
      id: item.word_id,
      type: "vocabulary",
      title: item.word,
      detail: [item.part_of_speech, item.definition]
        .filter((part) => part && String(part).trim())
        .join(" · "),
      date: item.created_at || item.date,
    }));

    const lines = sentences.map((item) => ({
      key: `sentence-${item.id_upload || item.upload_id || item.id}`,
      id: item.id_upload || item.upload_id || item.id,
      type: "sentence",
      title: item.sentence,
      detail: [item.sentence_type, item.course_title || item.course]
        .filter((part) => part && String(part).trim())
        .join(" · "),
      date: item.created_at,
    }));

    return [...words, ...lines].sort((a, b) => {
      const left = new Date(a.date || 0).getTime();
      const right = new Date(b.date || 0).getTime();
      if (right !== left) return right - left;
      return String(b.key).localeCompare(String(a.key));
    });
  }, [vocabulary, sentences]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter !== "all" && row.type !== filter) return false;
      if (!needle) return true;
      return `${row.title || ""} ${row.detail || ""}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, filter, search]);

  // paging restarts whenever the filter or the search changes
  useEffect(() => {
    setShown(PAGE_SIZE);
  }, [filter, search]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      vocabulary: rows.filter((row) => row.type === "vocabulary").length,
      sentence: rows.filter((row) => row.type === "sentence").length,
    }),
    [rows]
  );

  const paged = visible.slice(0, shown);
  const remaining = visible.length - paged.length;

  const remove = async (row) => {
    setDeleting(row.key);
    try {
      const endpoint =
        row.type === "vocabulary"
          ? `${API}/dictionnary/${row.id}`
          : `${API}/userupload/${row.id}`;
      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (row.type === "vocabulary") {
        setVocabulary((items) => items.filter((i) => i.word_id !== row.id));
      } else {
        setSentences((items) =>
          items.filter(
            (i) => (i.id_upload || i.upload_id || i.id) !== row.id
          )
        );
      }
    } catch (err) {
      setError("Could not delete that entry.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 px-2 py-10 md:px-0">
      <div className="mx-auto max-w-5xl">
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="flex items-center gap-3 text-4xl font-extrabold tracking-tight text-gray-900">
            <DocumentTextIcon className="h-10 w-10 text-indigo-500 drop-shadow" />
            My Notepad
          </h1>
          <p className="mt-2 max-w-2xl text-lg text-gray-500">
            {view === "notes"
              ? "Everything you have saved, newest first: the words you looked up and the sentences you wrote."
              : "How far you have got in each course, graded red to green."}
          </p>
        </motion.div>

        {isGuest && showGuestNudge && (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
            <p className="text-sm text-indigo-900">
              You are working as a guest: these notes are kept in this browser.
              Create an account and they follow you to any device.
            </p>
            <div className="flex items-center gap-3">
              <Link
                to="/login_register"
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                Create an account
              </Link>
              <button
                onClick={() => setShowGuestNudge(false)}
                className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
              >
                Not now
              </button>
            </div>
          </div>
        )}

        {/* two jobs, two views — not four tabs */}
        <div className="mb-5 inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
          {[
            { id: "notes", label: "My notes", icon: DocumentTextIcon },
            { id: "progress", label: "Progress", icon: ChartPieIcon },
          ].map((option) => {
            const Icon = option.icon;
            const active = view === option.id;
            return (
              <button
                key={option.id}
                onClick={() => setView(option.id)}
                className={`relative inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  active ? "text-white" : "text-gray-600 hover:text-indigo-600"
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="notepad-view-pill"
                    className="absolute inset-0 rounded-lg bg-indigo-600"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <Icon className="relative h-4 w-4" />
                <span className="relative">{option.label}</span>
                {option.id === "notes" && (
                  <span
                    className={`relative text-xs ${
                      active ? "text-indigo-100" : "text-gray-400"
                    }`}
                  >
                    {counts.all}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {view === "progress" ? (
          <CourseProgress onCourseClick={() => {}} />
        ) : (
          <>
        {/* filters, search, refresh */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {FILTERS.map((option) => (
            <button
              key={option.id}
              onClick={() => setFilter(option.id)}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                filter === option.id
                  ? "border-indigo-500 bg-indigo-500 text-white shadow-sm"
                  : "border-gray-300 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              {option.label}
              <span
                className={`ml-1.5 text-xs ${
                  filter === option.id ? "text-indigo-100" : "text-gray-400"
                }`}
              >
                {counts[option.id]}
              </span>
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search your notes…"
                className="w-48 rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-indigo-400 focus:outline-none sm:w-64"
              />
            </div>
            <button
              onClick={load}
              title="Refresh"
              className="rounded-full p-2 text-gray-400 transition hover:bg-white hover:text-gray-600"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* the one table */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {filter === "all" && (
                    <th className="px-4 py-3 font-semibold text-gray-600">
                      Type
                    </th>
                  )}
                  <th className="px-4 py-3 font-semibold text-gray-600">Entry</th>
                  <th className="px-4 py-3 font-semibold text-gray-600">Detail</th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-600">
                    Added
                  </th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((row) => {
                  const meta = TYPES[row.type];
                  const Icon = meta.icon;
                  return (
                    <tr key={row.key} className="align-top hover:bg-indigo-50/40">
                      {filter === "all" && (
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.chip}`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {meta.label}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <span className="block max-w-md break-words">
                          {row.title}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        <span className="block max-w-lg break-words">
                          {row.detail || "—"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                        {when(row.date)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          onClick={() => remove(row)}
                          disabled={deleting === row.key}
                          title="Delete"
                          className="rounded p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!loading && visible.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              {rows.length === 0
                ? "Nothing saved yet. Double-click any word on a lesson page and it lands here."
                : "Nothing matches that filter."}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-gray-500">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Loading your notepad…
            </div>
          )}

          {/* paging: a count and one quiet control, no page numbers to hunt through */}
          {visible.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/70 px-4 py-3 text-sm text-gray-500">
              <span>
                Showing {paged.length} of {visible.length}
                {visible.length !== rows.length &&
                  ` (filtered from ${rows.length})`}
              </span>
              <div className="flex items-center gap-3">
                {shown > PAGE_SIZE && (
                  <button
                    onClick={() => setShown(PAGE_SIZE)}
                    className="font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Show less
                  </button>
                )}
                {remaining > 0 && (
                  <>
                    <button
                      onClick={() => setShown((n) => n + PAGE_SIZE)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 transition hover:border-indigo-300 hover:text-indigo-600"
                    >
                      Show more
                    </button>
                    {remaining > PAGE_SIZE && (
                      <button
                        onClick={() => setShown(visible.length)}
                        className="font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        Show all {visible.length}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

NotepadOverview.propTypes = {
  initialFilter: PropTypes.oneOf(["all", "vocabulary", "sentence"]),
  initialView: PropTypes.oneOf(["notes", "progress"]),
};

export default NotepadOverview;
